import { PressableScale } from "@/components/prooftree/PressableScale"
import { Box } from "@/components/ui/box"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { Colors } from "@/constants/Colors"
import type { AuthFlowMode } from "@/services/authHelpers"
import {
  CheckCircle2,
  Eye,
  KeyRound,
  Link as LinkIcon,
  LogOut,
  Mail,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react-native"
import { createElement, type ReactNode } from "react"
import { Platform, Pressable, TextInput, type KeyboardTypeOptions } from "react-native"

type AuthModeOption = {
  mode: AuthFlowMode
  label: string
}

export function AuthHero({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <Box
      className="rounded-2xl border p-5"
      style={{
        backgroundColor: `${Colors.dark.primary}14`,
        borderColor: `${Colors.dark.primary}3D`,
      }}
      testID="auth-hero"
    >
      <HStack className="items-start gap-3">
        <Box
          className="h-12 w-12 items-center justify-center rounded-2xl"
          style={{ backgroundColor: `${Colors.dark.primary}24` }}
        >
          <ShieldCheck color={Colors.dark.primary} size={22} />
        </Box>
        <VStack className="flex-1 gap-2">
          <Text className="text-xl font-bold text-white">{title}</Text>
          <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
            {subtitle}
          </Text>
        </VStack>
      </HStack>
    </Box>
  )
}

export function AuthModeSelector({
  mode,
  options,
  onChange,
}: {
  mode: AuthFlowMode
  options: AuthModeOption[]
  onChange: (mode: AuthFlowMode) => void
}) {
  return (
    <HStack className="gap-2" testID="auth-mode-selector">
      {options.map((option) => {
        const active = mode === option.mode
        return (
          <PressableScale
            key={option.mode}
            accessibilityRole="button"
            accessibilityLabel={`Show ${option.label} auth form`}
            className="flex-1"
            testID={`auth-mode-${option.mode}`}
            onPress={() => onChange(option.mode)}
          >
            <Box
              className="min-h-11 items-center justify-center rounded-full border px-3 py-2"
              style={{
                backgroundColor: active ? `${Colors.dark.primary}24` : Colors.dark.surfaceSecondary,
                borderColor: active ? Colors.dark.primary : "rgba(255,255,255,0.06)",
              }}
            >
              <Text
                className="text-center text-xs font-bold"
                style={{ color: active ? Colors.dark.primary : Colors.dark.textSecondary }}
              >
                {option.label}
              </Text>
            </Box>
          </PressableScale>
        )
      })}
    </HStack>
  )
}

export function AuthField({
  label,
  value,
  placeholder,
  onChangeText,
  secureTextEntry,
  keyboardType,
  autoCapitalize = "none",
  error,
  testID,
  icon = "key",
}: {
  label: string
  value: string
  placeholder: string
  onChangeText: (value: string) => void
  secureTextEntry?: boolean
  keyboardType?: KeyboardTypeOptions
  autoCapitalize?: "none" | "sentences" | "words" | "characters"
  error?: string
  testID: string
  icon?: "email" | "key" | "user"
}) {
  const Icon = icon === "email" ? Mail : icon === "user" ? UserRound : KeyRound

  return (
    <VStack className="gap-2">
      <Text className="font-bold text-white">{label}</Text>
      <Box
        className="rounded-2xl border px-4 py-3"
        style={{
          backgroundColor: Colors.dark.surfaceSecondary,
          borderColor: error ? Colors.dark.proof : "rgba(255,255,255,0.08)",
        }}
      >
        <HStack className="items-center gap-3">
          <Icon color={error ? Colors.dark.proof : Colors.dark.textSecondary} size={18} />
          <TextInput
            className="min-h-8 flex-1 text-white"
            accessibilityLabel={label}
            autoCapitalize={autoCapitalize}
            keyboardType={keyboardType}
            placeholder={placeholder}
            placeholderTextColor={Colors.dark.textSecondary}
            secureTextEntry={secureTextEntry}
            testID={testID}
            value={value}
            onChangeText={onChangeText}
          />
          {secureTextEntry ? <Eye color={Colors.dark.textSecondary} size={16} /> : null}
        </HStack>
      </Box>
      {error ? (
        <Text className="text-sm" style={{ color: Colors.dark.proof }} testID={`${testID}-error`}>
          {error}
        </Text>
      ) : null}
    </VStack>
  )
}

export function StatusMessage({
  message,
  tone,
  testID = "auth-status-message",
}: {
  message: string
  tone: "info" | "warning" | "success"
  testID?: string
}) {
  const color =
    tone === "warning"
      ? Colors.dark.proof
      : tone === "success"
        ? Colors.dark.growth
        : Colors.dark.primary
  const testProps =
    Platform.OS === "web" ? ({ "data-testid": testID } as Record<string, string>) : { testID }

  return (
    <Box
      {...testProps}
      className="rounded-2xl border p-4"
      style={{ backgroundColor: `${color}14`, borderColor: `${color}44` }}
    >
      <Text className="text-sm leading-5" style={{ color: Colors.dark.textPrimary }}>
        {message}
      </Text>
    </Box>
  )
}

export function PrimaryAuthButton({
  children,
  disabled,
  testID,
  onPress,
}: {
  children: ReactNode
  disabled?: boolean
  testID: string
  onPress: () => void
}) {
  return (
    <ActionButton
      label={children}
      color={Colors.dark.primary}
      disabled={disabled}
      foregroundColor={Colors.dark.primaryForeground}
      icon={<KeyRound color={Colors.dark.primaryForeground} size={18} />}
      testID={testID}
      onPress={onPress}
    />
  )
}

export function OAuthButton({
  label,
  disabled,
  secondary,
  testID,
  onPress,
}: {
  label: string
  disabled?: boolean
  secondary?: boolean
  testID: string
  onPress: () => void
}) {
  const color = secondary ? Colors.dark.textSecondary : Colors.dark.primary

  return (
    <ActionButton
      label={label}
      accessibilityLabel={`Continue with ${label}`}
      borderColor={`${color}66`}
      className="flex-1"
      color="transparent"
      disabled={disabled}
      foregroundColor={color}
      icon={<LinkIcon color={color} size={16} />}
      testID={testID}
      onPress={onPress}
    />
  )
}

export function SignedInPanel({
  displayName,
  email,
  message,
  actionLoading,
  onOpenPassport,
  onSignOut,
}: {
  displayName: string
  email?: string | null
  message?: string
  actionLoading?: boolean
  onOpenPassport: () => void
  onSignOut: () => void
}) {
  return (
    <VStack className="gap-5" testID="auth-signed-in-panel">
      <Box
        className="rounded-2xl border p-5"
        style={{
          backgroundColor: `${Colors.dark.growth}17`,
          borderColor: `${Colors.dark.growth}55`,
        }}
      >
        <HStack className="items-start gap-3">
          <Box
            className="h-12 w-12 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${Colors.dark.growth}24` }}
          >
            <CheckCircle2 color={Colors.dark.growth} size={24} />
          </Box>
          <VStack className="flex-1 gap-2">
            <Text className="text-2xl font-bold text-white">{displayName}</Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              Signed in as {email}. Proofs and tasks can now sync to Supabase.
            </Text>
          </VStack>
        </HStack>
      </Box>

      {message ? <StatusMessage tone="info" message={message} /> : null}

      <ActionButton
        label="Open Passport"
        color={Colors.dark.primary}
        foregroundColor={Colors.dark.primaryForeground}
        icon={<Sparkles color={Colors.dark.primaryForeground} size={18} />}
        testID="auth-open-passport"
        onPress={onOpenPassport}
      />

      <ActionButton
        label="Sign Out"
        borderColor={`${Colors.dark.textSecondary}55`}
        color="transparent"
        disabled={actionLoading}
        foregroundColor={Colors.dark.textSecondary}
        icon={<LogOut color={Colors.dark.textSecondary} size={18} />}
        testID="auth-sign-out"
        onPress={onSignOut}
      />
    </VStack>
  )
}

function ActionButton({
  label,
  accessibilityLabel,
  color,
  foregroundColor,
  borderColor,
  className,
  disabled,
  icon,
  testID,
  onPress,
}: {
  label: ReactNode
  accessibilityLabel?: string
  color: string
  foregroundColor: string
  borderColor?: string
  className?: string
  disabled?: boolean
  icon: ReactNode
  testID: string
  onPress: () => void
}) {
  if (Platform.OS === "web") {
    return createElement(
      "button",
      {
        "aria-label": accessibilityLabel ?? (typeof label === "string" ? label : undefined),
        "data-testid": testID,
        disabled,
        onClick: onPress,
        onClickCapture: onPress,
        onMouseUp: onPress,
        onMouseUpCapture: onPress,
        onPointerUp: onPress,
        onPointerUpCapture: onPress,
        style: {
          alignItems: "center",
          backgroundColor: color,
          borderColor: borderColor ?? color,
          borderRadius: 16,
          borderStyle: "solid",
          borderWidth: 1,
          color: foregroundColor,
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          flex: className?.includes("flex-1") ? 1 : undefined,
          fontSize: 18,
          fontWeight: 700,
          gap: 8,
          justifyContent: "center",
          minHeight: 48,
          opacity: disabled ? 0.45 : 1,
          paddingInline: 24,
          width: "100%",
        },
        testid: testID,
        type: "button",
      },
      createElement("span", { style: { color: foregroundColor } }, label),
    )
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? (typeof label === "string" ? label : undefined)}
      className={className}
      disabled={disabled}
      testID={testID}
      style={{ opacity: disabled ? 0.45 : 1 }}
      onPress={onPress}
    >
      <Box
        className="min-h-12 flex-row items-center justify-center gap-2 rounded-2xl border px-6"
        style={{
          backgroundColor: color,
          borderColor: borderColor ?? color,
        }}
      >
        {icon}
        <Text className="text-lg font-bold" style={{ color: foregroundColor }}>
          {label}
        </Text>
      </Box>
    </Pressable>
  )
}
